import { useContext, useMemo } from 'react';

import { useTheme } from '@mui/material';
import {
  getSearchTextMatchHighlightSpan,
  type SearchTextMatchPriorityOptions,
} from '@kc-console/core';

import { TextHighlightSuppressContext } from './textHighlightSuppressContext';

import type { ColorKey } from '../../themes/theme/color';
import type { PropsWithClassName, PropsWithStyle } from '../../types';

export interface TextHighlightsProps extends PropsWithClassName, PropsWithStyle {
  highlightText: string;
  originText: string;
  color?: ColorKey | (string & {});
  /**
   * 검색 매칭·정렬(`getSearchTextMatchSortMeta`)과 동일한 정규화 규칙. 미지정 시 기본값(trim·대소문자 무시).
   */
  matchOptions?: SearchTextMatchPriorityOptions;
}

const TextHighlights = (props: TextHighlightsProps) => {
  const {
    color = 'text/informative/default',
    highlightText,
    matchOptions,
    originText,
    ...others
  } = props;
  const theme = useTheme();
  const suppressed = useContext(TextHighlightSuppressContext);

  const parts = useMemo(() => {
    if (suppressed) {
      return [{ highlight: false as const, text: originText }];
    }
    const span = getSearchTextMatchHighlightSpan(originText, highlightText, matchOptions);
    if (!span) {
      return [{ highlight: false as const, text: originText }];
    }
    const { end, start } = span;
    const chunks: { highlight: boolean; text: string }[] = [];
    if (start > 0) chunks.push({ highlight: false, text: originText.slice(0, start) });
    chunks.push({ highlight: true, text: originText.slice(start, end) });
    if (end < originText.length) chunks.push({ highlight: false, text: originText.slice(end) });
    return chunks;
  }, [highlightText, matchOptions, originText, suppressed]);

  const highlighColor = color in theme.color ? theme.color[color as ColorKey] : color;

  return (
    <span {...others}>
      {parts.map(({ text, highlight }, index) => (
        <span key={index} style={{ color: highlight ? highlighColor : undefined }}>
          {text}
        </span>
      ))}
    </span>
  );
};

export default TextHighlights;
